import React, { FC, useState } from "react";
import { toast } from "react-toastify";
import { Row, Col } from "react-bootstrap";
import Popup from "reactjs-popup";

import apiCalls from "../../PowerShadesAPIFunctions";
import Button from "../../Parts/Button";
import { GetCreditLimitCheck, QuoteStatusTypes } from "../../powershadesApiTypes";
import ApprovalModal from "../../Modals/ApprovalModal";
import { useIsService } from "../../Store/user/hooks";
import { useIsPsAdmin } from "../../hooks";

type ApproveOrderButtonProps = {
	approveOrderOpen?: boolean;
	quoteId: number;
	quoteType: string;
	onModalClose?: () => void;
	orderStatus?: QuoteStatusTypes;
	setApproveOrderOpen: (open: boolean) => void;
};

const ApproveOrderButton: FC<ApproveOrderButtonProps> = ({
	approveOrderOpen,
	quoteId,
	quoteType,
	onModalClose,
	setApproveOrderOpen,
	orderStatus = undefined
}) => {
	const [approveOrderLoading, setApproveOrderLoading] = useState(false);

	const [creditLimitWarning, setCreditLimitWarning] = useState(false);

	const isService = useIsService();
	const isAdmin = useIsPsAdmin();

	// Initialize with defaults
	const [creditLimitData, setCreditLimitData] = useState<GetCreditLimitCheck>(() => ({
		quote_id: 0,
		current_outstanding_amount: 0,
		credit_limit: 0,
		customer_name: "",
		expected_invoice_total: 0,
		credit_check_passed: true,
		contact_url: ""
	}));

	const {
		credit_limit: creditLimit,
		customer_name: buyerName,
		expected_invoice_total: requestedInvoiceAmount,
		current_outstanding_amount: currenCredit,
		contact_url: contactUrl
	} = creditLimitData;

	const updatedCredit = currenCredit + requestedInvoiceAmount;
	const remainingCredit = creditLimit - updatedCredit;

	const handleApproveOrderButtonClicked = () => {
		let creditLimitOk = false;
		setApproveOrderLoading(true);

		const open = () => {
			setApproveOrderOpen(true);
		};

		if (orderStatus === "order_pending_approval") {
			apiCalls.getCreditLimit(quoteId).then((resp) => {
				const creditLimitDataResp = resp.data.credit_check;

				if (creditLimitDataResp.credit_check_passed) {
					creditLimitOk = true;
				}

				if (creditLimitOk) {
					setApproveOrderOpen(true);
				} else {
					setCreditLimitData(creditLimitDataResp);
					setCreditLimitWarning(true);
				}

				setApproveOrderLoading(false);
			});
		} else {
			open();
		}


	};

	const handleApproveOrder = () => {
		setApproveOrderLoading(true);
		apiCalls.accountingApproveOrder(quoteId).then((_response) => {
			setApproveOrderLoading(false);
			setApproveOrderOpen(false);
			onModalClose?.();

			if (_response.status === 200) {
				window.location.reload();
			} else {
				toast.error("Error approving order. Please Contact Engineering.");
			}
		});
	};

	const handleEmailTerritoryManager = () => {
		apiCalls.sendAccountingHoldEmail(quoteId).then((_response) => {
			if (_response.data.success) {
				toast.success("Email sent to Territory Manager");
			} else {
				toast.error("Error sending email. Please Contact Engineering.");
			}
		});
	};

	return (
		<>
			<Col xl="auto" lg={3} md={3} sm={4} xs={6} className="mt-2 mt-xl-0">
				<Button
					title="Export Outdoor Shades"
					color={quoteType}
					fluid
					onClick={handleApproveOrderButtonClicked}
					loading={approveOrderLoading}
				>
					Approve Order
				</Button>
			</Col>
			{approveOrderOpen && (
				orderStatus === "awaiting_sales_approval"
					? <ApprovalModal
						isOpen={approveOrderOpen}
						onClose={() => {
							setApproveOrderOpen(false);
							onModalClose?.();
						}}
						quoteId={quoteId}
						type="sales"
						key="sales-approval-modal"
					/>
					: orderStatus === "under_review" && (isService || isAdmin)
						? <ApprovalModal
							isOpen={approveOrderOpen}
							onClose={() => {
								setApproveOrderOpen(false);
								onModalClose?.();
							}}
							quoteId={quoteId}
							type="service"
							onApprove={() => {
								setApproveOrderOpen(false);
								onModalClose?.();
							}}
							key="service-approval-modal"
						/>
						: <Popup
							open={approveOrderOpen}
							onClose={() => {
								setApproveOrderOpen(false);
								onModalClose?.();
							}}
							modal
							className="mobile-modal"
						>
							<Row>
								<Col>
									<Row>
										<Col>
											<Row>
												<Col>
													<h3>Approve Order</h3>
												</Col>
											</Row>
											<Row>
												<Col>
													<p>
														Are you sure you want to approve this order?
													</p>
												</Col>
											</Row>
											<div className="d-flex justify-content-between gap-3 pt-4">
												<Button color="gray" onClick={() => setApproveOrderOpen(false)}>
													Cancel
												</Button>
												<Button
													onClick={handleApproveOrder}
													loading={approveOrderLoading}
												>
													Approve Order
												</Button>
											</div>
										</Col>
									</Row>
								</Col>
							</Row>
						</Popup>
			)}
			<Popup
				open={creditLimitWarning}
				onClose={() => {
					setCreditLimitWarning(false);
					onModalClose?.();
				}}
				modal
				className="mobile-modal"
			>
				<Row>
					<Col>
						<Row>
							<Col>
								<h4>
									<a
										href={contactUrl}
									>
										{buyerName}
										{" "}
										-
										{" "}
										Credit Limit Verification
									</a>
								</h4>
							</Col>
						</Row>
						<Row>
							<Col>
								<h6>Account Credit Limit</h6>

							</Col>
							<Col>
								{creditLimit}
							</Col>
						</Row>
						<Row>
							<Col>
								<h6>Current Credit</h6>
							</Col>
							<Col>
								{currenCredit}
							</Col>
						</Row>
						<Row>
							<Col>
								<h6>Updated Credit</h6>
							</Col>
							<Col>
								{updatedCredit}
							</Col>
						</Row>
						<Row>
							<Col>
								<h6>Remaining Credit</h6>
							</Col>
							<Col>
								{remainingCredit}
							</Col>
						</Row>
						<Row>
							<Col>
								<Button
									color="gray"
									onClick={() => {
										setCreditLimitWarning(false);
										onModalClose?.();
									}}
								>
									Cancel
								</Button>
							</Col>
							<Col>
								<Button
									onClick={handleEmailTerritoryManager}
									color="gray"
								>
									Email Territory Manager
								</Button>
							</Col>
							<Col>
								<Button
									onClick={handleApproveOrder}
								>
									Confirm and Approve
								</Button>
							</Col>

						</Row>

					</Col>
				</Row>

			</Popup>

		</>
	);
};

export default ApproveOrderButton;

